import { FC, useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import luhn from 'fast-luhn';
import { findSelectedCategoryByKey, findSelectedSubcategoryByKey } from '../../../helper/cms';
import { ResourceText, ShopContent } from '../../../types/cms_shared_types';
import { UserDateInput } from '../../Common/UserDateInput';
import { Conditional } from '../../Common/Conditional';
import { Step } from '../Step';
import { InvoiceInformation } from './InvoiceInformation';
import { RadioDialog } from './RadioDialog';
import { LineDelimiter } from '../../Common/LineDelimiter';
import { UserBooleanInput } from '../../Common/UserBooleanInput';
import { DefaultBottomBar } from '../BottomBar/DefaultBottomBar';
import { DEFAULT_COUNTRY_KEY } from './countries';
import { EMAIL_REGEX, LETTERS_ONLY_REGEX, SWEDISH_POST_CODE_REGEX } from '../../../helper/helper';
import { EdiFormat, InvoiceFormInputs, InvoiceMethod } from '../../../types/api_shared_types';
import * as Sentry from '@sentry/react';
import { InvoiceMethods } from './InvoiceMethods';
import { FormikPersist } from '../../Common/FormikPersist';

export enum Period {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  HALFYEARLY = 'halfyearly',
  YEARLY = 'yearly',
}

const DEFAULT_MONTHLY_INVOICE_THRESHOLD = 300;
const FALLBACK_INVOICE_PERIOD = Period.YEARLY;
const ORGANIZATION_NUMBER_REGEX = /^\d{6}-?\d{4}$/;
const PEPPOL_ID_NUMBER_REGEX = /^(0007:\d{10}|0088:\d{13})$/;
const GLN_NUMBER_REGEX = /^\d{13}$/;

interface InvoiceProps {
  price: string;
  selectedCategoryKey?: string;
  selectedSubcategoryKey?: string;
  invoiceFormInputs: InvoiceFormInputs;
  setInvoiceFormInputs: (inputs: InvoiceFormInputs) => void;
  atLeastOneReportingProduct: boolean;
  addonsForceMonthlyInvoicing: boolean;
  nextButtonClicked: () => void;
  cmsShopContent: ShopContent;
  language: string;
  registerOnBack?: (callback: () => void) => void;
}

export const Invoice: FC<InvoiceProps> = ({
  price,
  selectedCategoryKey,
  selectedSubcategoryKey,
  invoiceFormInputs,
  setInvoiceFormInputs,
  atLeastOneReportingProduct,
  addonsForceMonthlyInvoicing,
  nextButtonClicked,
  cmsShopContent,
  language,
  registerOnBack,
}) => {
  const [enableScroll, setEnableScroll] = useState(false);
  const [availablePeriods, setAvailablePeriods] = useState<ResourceText[]>([]);

  const scrollRef = useRef<HTMLInputElement>(null);

  const isPriceBelowMonthlyThreshold =
    Number.parseInt(price.replace(/\s/g, '')) <
    (cmsShopContent.invoiceStep.monthlyInvoiceThreshold ?? DEFAULT_MONTHLY_INVOICE_THRESHOLD);

  const executeScroll = () => scrollRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

  const fetchDefaultInvoicePeriod = (): string => {
    const category = selectedCategoryKey
      ? findSelectedCategoryByKey(cmsShopContent.categories, selectedCategoryKey)
      : undefined;
    const subcategory =
      category && selectedSubcategoryKey
        ? findSelectedSubcategoryByKey(selectedSubcategoryKey, category.subcategories)
        : undefined;
    const defaultInvoicePeriod =
      subcategory?.defaultInvoicePeriod && subcategory?.defaultInvoicePeriod !== ''
        ? subcategory.defaultInvoicePeriod
        : Period.YEARLY;

    if (addonsForceMonthlyInvoicing) {
      return Period.MONTHLY;
    }

    if (isPriceBelowMonthlyThreshold && defaultInvoicePeriod === Period.MONTHLY) {
      return FALLBACK_INVOICE_PERIOD;
    }
    return defaultInvoicePeriod;
  };

  const getDefaultPeriodInCorrectLanguage = (): string => {
    const defaultPeriod = fetchDefaultInvoicePeriod();
    return cmsShopContent.invoiceStep.invoicePeriods.find((p) => p.key === defaultPeriod)?.value ?? '';
  };

  const getInvoicePeriodText = () => {
    if (addonsForceMonthlyInvoicing && atLeastOneReportingProduct) {
      Sentry.captureMessage(
        'Both addonsForceMonthlyInvoicing and atLeastOneReportingProduct are true (invalid state).',
      );
    }

    if (atLeastOneReportingProduct) {
      return cmsShopContent.invoiceStep.defaultInvoicePeriodForReporting.replace(
        '{default}',
        getDefaultPeriodInCorrectLanguage().toLowerCase(),
      );
    }

    if (addonsForceMonthlyInvoicing) {
      return cmsShopContent.invoiceStep.forceMonthlyInvoicing;
    }
  };

  const postCodeValidation = (field: string) => {
    return Yup.string()
      .required(cmsShopContent.inputRequiredError)
      .when(field, {
        is: DEFAULT_COUNTRY_KEY?.sv,
        then: (schema) => schema.matches(SWEDISH_POST_CODE_REGEX, cmsShopContent.invalidPostCodeError),
        otherwise: (schema) => schema.required(cmsShopContent.inputRequiredError),
      });
  };

  const validationSchema = Yup.object().shape({
    organisationNumberInput: Yup.string()
      .matches(ORGANIZATION_NUMBER_REGEX, cmsShopContent.invalidOrganisationNumberError)
      .test('luhn-validation', cmsShopContent.invalidLuhnOrganisationNumberError, (value) => {
        if (!value) {
          return false;
        }
        const val = (value as string).replace('-', '');
        return luhn(val);
      })
      .required(cmsShopContent.inputRequiredError),
    companyNameInput: Yup.string().required(cmsShopContent.inputRequiredError),
    companyAddressInput: Yup.string().required(cmsShopContent.inputRequiredError),
    companyPostNumberInput: postCodeValidation('companyCountryInput'),
    companyCityInput: Yup.string()
      .matches(LETTERS_ONLY_REGEX, cmsShopContent.invalidCityError)
      .required(cmsShopContent.inputRequiredError),
    companyCountryInput: Yup.string().required(cmsShopContent.inputRequiredError),

    sameInvoiceAddressInput: Yup.boolean(),

    invoiceNameInput: Yup.string().notRequired(),
    invoiceAddressInput: Yup.string().when('sameInvoiceAddressInput', {
      is: true,
      then: () => Yup.string().notRequired(),
      otherwise: () => Yup.string().required(cmsShopContent.inputRequiredError),
    }),
    invoicePostNumberInput: Yup.string().when('sameInvoiceAddressInput', {
      is: true,
      then: () => Yup.string().notRequired(),
      otherwise: () => postCodeValidation('invoiceCountryInput'),
    }),
    invoiceCityInput: Yup.string().when('sameInvoiceAddressInput', {
      is: true,
      then: () => Yup.string().notRequired(),
      otherwise: () =>
        Yup.string()
          .matches(LETTERS_ONLY_REGEX, cmsShopContent.invalidCityError)
          .required(cmsShopContent.inputRequiredError),
    }),
    invoiceCountryInput: Yup.string().when('sameInvoiceAddressInput', {
      is: true,
      then: () => Yup.string().notRequired(),
      otherwise: () => Yup.string().required(cmsShopContent.inputRequiredError),
    }),

    startDateInput: Yup.string().required(cmsShopContent.inputRequiredError),
    invoicePeriodInput: Yup.string().required(cmsShopContent.inputRequiredError),
    emailAddressPdfInput: Yup.string().when('selectedInvoiceMethod', {
      is: InvoiceMethod.EMAIL,
      then: (schema) =>
        schema.matches(EMAIL_REGEX, cmsShopContent.invalidEmailError).required(cmsShopContent.inputRequiredError),
      otherwise: (schema) => schema.notRequired(),
    }),
    selectedInvoiceMethod: Yup.string()
      .oneOf(Object.values(InvoiceMethod) as InvoiceMethod[])
      .required(cmsShopContent.inputRequiredError),
    ediFormat: Yup.string().when('selectedInvoiceMethod', {
      is: InvoiceMethod.EDI,
      then: (schema) =>
        schema.oneOf(Object.values(EdiFormat) as EdiFormat[]).required(cmsShopContent.inputRequiredError),
      otherwise: (schema) => schema.notRequired(),
    }),
    ediReference: Yup.string().when('selectedInvoiceMethod', {
      is: InvoiceMethod.EDI,
      then: (schema) => schema.required(cmsShopContent.inputRequiredError),
      otherwise: (schema) => schema.notRequired(),
    }),
    peppolId: Yup.string().when(['selectedInvoiceMethod', 'ediFormat'], {
      is: (selectedInvoiceMethod: InvoiceMethod, ediFormat: EdiFormat) =>
        selectedInvoiceMethod === InvoiceMethod.EDI && ediFormat === EdiFormat.PEPPOL,
      then: (schema) =>
        schema
          .matches(PEPPOL_ID_NUMBER_REGEX, cmsShopContent.invoiceStep.ediInvoice.invalidPeppolIdFormat)
          .required(cmsShopContent.inputRequiredError),
      otherwise: (schema) => schema.notRequired(),
    }),
    ediOperator: Yup.string().when(['selectedInvoiceMethod', 'ediFormat'], {
      is: (selectedInvoiceMethod: InvoiceMethod, ediFormat: EdiFormat) =>
        selectedInvoiceMethod === InvoiceMethod.EDI && ediFormat === EdiFormat.SVEFAKTURA,
      then: (schema) => schema.required(cmsShopContent.inputRequiredError),
      otherwise: (schema) => schema.notRequired(),
    }),
    gln: Yup.string().when('ediFormat', {
      is: EdiFormat.SVEFAKTURA,
      then: (schema) =>
        schema.matches(GLN_NUMBER_REGEX, cmsShopContent.invoiceStep.ediInvoice.invalidGlnFormat).notRequired(),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  useEffect(() => {
    if (enableScroll) {
      executeScroll();
    }
  }, [enableScroll]);

  useEffect(() => {
    if (isPriceBelowMonthlyThreshold) {
      setAvailablePeriods(cmsShopContent.invoiceStep.invoicePeriods.filter((p) => p.key !== Period.MONTHLY) ?? []);
    } else {
      setAvailablePeriods(cmsShopContent.invoiceStep.invoicePeriods ?? []);
    }
  }, [atLeastOneReportingProduct, isPriceBelowMonthlyThreshold, cmsShopContent.invoiceStep.invoicePeriods]);

  return (
    <Formik
      onSubmit={(values) => {
        setInvoiceFormInputs(values);
        nextButtonClicked();
      }}
      initialValues={{
        organisationNumberInput: invoiceFormInputs.organisationNumberInput,
        companyNameInput: invoiceFormInputs.companyNameInput,
        companyAddressInput: invoiceFormInputs.companyAddressInput,
        companyPostNumberInput: invoiceFormInputs.companyPostNumberInput,
        companyCityInput: invoiceFormInputs.companyCityInput,
        companyCountryInput:
          invoiceFormInputs.companyCountryInput === ''
            ? DEFAULT_COUNTRY_KEY?.sv ?? 'Sverige'
            : invoiceFormInputs.companyCountryInput,

        sameInvoiceAddressInput: invoiceFormInputs.sameInvoiceAddressInput,

        invoiceCareOfInput: invoiceFormInputs.invoiceCareOfInput,
        invoiceAddressInput: invoiceFormInputs.invoiceAddressInput,
        invoicePostNumberInput: invoiceFormInputs.invoicePostNumberInput,
        invoiceCityInput: invoiceFormInputs.invoiceCityInput,
        invoiceCountryInput: invoiceFormInputs.invoiceCountryInput,

        startDateInput: invoiceFormInputs.startDateInput,
        invoicePeriodInput:
          invoiceFormInputs.invoicePeriodInput === ''
            ? fetchDefaultInvoicePeriod()
            : invoiceFormInputs.invoicePeriodInput,
        emailAddressPdfInput: invoiceFormInputs.emailAddressPdfInput,
        selectedInvoiceMethod: invoiceFormInputs.selectedInvoiceMethod,
        ediFormat: invoiceFormInputs.ediFormat,
        ediReference: invoiceFormInputs.ediReference,
        peppolId: invoiceFormInputs.peppolId,
        ediOperator: invoiceFormInputs.ediOperator,
        gln: invoiceFormInputs.gln,
      }}
      validationSchema={validationSchema}
      validateOnMount
    >
      {({ isValid, values, setFieldValue, setValues, submitForm }) => (
        <>
          <FormikPersist<InvoiceFormInputs> registerOnBack={registerOnBack} onSave={setInvoiceFormInputs} />
          <Step
            fullWidth
            title={cmsShopContent.invoiceStep.title}
            subtitle={cmsShopContent.invoiceStep.subtitle}
            content={
              <div className="flex flex-col pb-8 mx-auto sm:flex-row place-content-center gap-y-8">
                <div className="sm:w-1/2 sm:max-w-[600px] flex flex-col items-center sm:ml-14 sm:mr-8 gap-y-2">
                  <InvoiceInformation language={language} cmsShopContent={cmsShopContent} />

                  <UserBooleanInput
                    name="sameInvoiceAddressInput"
                    title={cmsShopContent.invoiceStep.invoiceAddressTitle}
                    description={cmsShopContent.invoiceStep.invoiceAddressDescription}
                    callback={(value: boolean) => setEnableScroll(!value)}
                  />

                  <Conditional
                    condition={!values.sameInvoiceAddressInput}
                    trueRender={
                      <>
                        <LineDelimiter />
                        <div ref={scrollRef} className="w-full">
                          <div className="mb-8 text-xl font-bold text-text-dark">
                            {cmsShopContent.invoiceStep.invoiceAddressTitle}
                          </div>
                          <InvoiceInformation isInvoice language={language} cmsShopContent={cmsShopContent} />
                        </div>
                      </>
                    }
                  />

                  <Conditional
                    condition={!values.sameInvoiceAddressInput}
                    trueRender={<LineDelimiter hideOnDesktop />}
                  />
                </div>

                <div className="sm:w-1/2 sm:max-w-[600px] flex flex-col items-center sm:ml-8 sm:mr-14 gap-y-8">
                  <UserDateInput
                    name="startDateInput"
                    prompt={cmsShopContent.invoiceStep.startDatePrompt}
                    allowPastDates
                  />
                  <RadioDialog
                    prompt={cmsShopContent.invoiceStep.invoicePeriodTitle}
                    options={availablePeriods}
                    selectedKey={values.invoicePeriodInput}
                    setSelectedKey={(val) => {
                      const newValues = {
                        ...values,
                        invoicePeriodInput: val as Period,
                      };

                      if (val === Period.MONTHLY && values.selectedInvoiceMethod === InvoiceMethod.PAPER) {
                        newValues.selectedInvoiceMethod = InvoiceMethod.EMAIL;
                      }

                      setValues(newValues);
                    }}
                    isSelected={(selectedKey: string, option: ResourceText): boolean => selectedKey === option.key}
                    informationText={
                      values.invoicePeriodInput === Period.MONTHLY && !addonsForceMonthlyInvoicing
                        ? cmsShopContent.invoiceStep.monthlyInvoiceExtraDescription
                        : ''
                    }
                    renderTextInsteadOfRadioItems={atLeastOneReportingProduct || addonsForceMonthlyInvoicing}
                    renderTextInsteadOfRadioItemsText={getInvoicePeriodText()}
                  />
                  <InvoiceMethods
                    cmsShopContent={cmsShopContent}
                    setInvoiceMethod={(value) => setFieldValue('selectedInvoiceMethod', value)}
                    values={values}
                  />
                </div>
              </div>
            }
            bottomBar={
              <DefaultBottomBar
                continueButtonDisabled={!isValid}
                onClick={submitForm}
                cmsShopContent={cmsShopContent}
                footerContent={cmsShopContent.invoiceStep.footer}
              />
            }
          />
        </>
      )}
    </Formik>
  );
};
